import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, CardBody, Button } from 'reactstrap';
import { toast } from 'react-hot-toast';
import OrderManagementService from '../../../../Services/QRCodeServices/Order Management/OrderManagementService';

import DeleteModal from '../../../../Components/Common/DeleteModal';
import { Triangle } from 'react-loader-spinner';
import { EditStatus } from '../../component/EditStatus';
import TableService from '../../../../Services/QRCodeServices/TableSerivce/TableService';

import Select from 'react-select';

export const MobileOrderPage = ({ onOrderSelect }) => {
  const navigate = useNavigate();
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [showImages, setShowImages] = useState(true);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 16;
  const maxImagesToShow = 5;
  const [editModal, setEditModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Calculate the first day of the current month and today's date
  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    // Set the dates in YYYY-MM-DD format for the input fields
    setStartDate(firstDayOfMonth.toISOString().split('T')[0]); // Set start date to the first day of the current month
    setEndDate(today.toISOString().split('T')[0]); // Set end date to today
  }, []);

  const statusColors = {
    Ongoing: "#FFA500", // Orange
    Billing: "#655bd1",
    Cancelled: "#FF0000", // Red
    Completed: "#15c46a", // Bright Green
  };

  const tableOptions = tables.map(table => ({
    value: table.id,
    label: table.identification
  }));

  useEffect(() => {
    fetchOrder();
    fetchTables();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchOrder();
    }, 120000);

    return () => clearInterval(interval);
  }, []);

  const fetchTables = async () => {
    try {
      const response = await TableService.getList();
      setTables(response);
    } catch (error) {
      toast.error("Error fetching tables");
    }
  };

  useEffect(() => {
    const filtered = data.filter((order) => {
      const matchesTable = !selectedTable ? true : order.tableData?.id === selectedTable.value;
      
      const orderDate = new Date(order.createdDate);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
      
      // Set time to midnight for accurate date comparison
      if (start) start.setHours(0, 0, 0, 0);
      if (end) end.setHours(23, 59, 59, 999);
      
      const matchesDateRange = (!start || orderDate >= start) && (!end || orderDate <= end);
      
      return matchesTable && matchesDateRange;
    });
    
    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [data, selectedTable, startDate, endDate]);

  // Reset filters function
  const resetFilters = () => {
    setSelectedTable(null);
    setStartDate("");
    setEndDate("");
  };

  // Validate date range
  const handleDateChange = (field, value) => {
    if (field === 'start') {
      setStartDate(value);
      if (endDate && new Date(value) > new Date(endDate)) {
        setEndDate(value);
      }
    } else {
      setEndDate(value);
      if (startDate && new Date(value) < new Date(startDate)) {
        setStartDate(value);
      }
    }
  };

  const handleOrderIdClick = (orderId) => {
    navigate(`/order-detail/${orderId}`);
  };

  const toggleExpandImages = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const toggleShowImages = () => {
    setShowImages(!showImages);
    setExpandedOrderId(null);
  };

  const toggleEditModal = () => {
    setEditModal(!editModal);
    if (!editModal) {
      setSelectedOrder(null);
      setEditStatus("");
    }
  };

  const handleEditClick = async (item) => {
    try {
      const orderDetails = await OrderManagementService.detail(item.id);
      setSelectedOrder(orderDetails);
      setEditStatus(orderDetails.orderStatus);
      setEditModal(true);
    } catch (error) {
      toast.error("Failed to fetch order details");
    }
  };

  const handleSaveEdit = async (remarks, customerInfo) => {
    try {
      await OrderManagementService.updateBillingStatus(selectedOrder.id, editStatus, remarks, customerInfo);
      toast.success("Order status updated successfully");
      fetchOrder();
      toggleEditModal();
    } catch (error) {
      toast.error("Failed to update the order status");
    }
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await OrderManagementService.delete(selectedDeleteItem.id);
        setData((prevList) =>
          prevList.filter((pkg) => pkg.id !== selectedDeleteItem.id)
        );
        toast.success("Order deleted successfully");
        fetchOrder();
      } catch (error) {
        toast.error("Failed to delete the record");
      } finally {
        toggleDeleteModal();
        setSelectedDeleteItem(null);
      }
    }
  };

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDeleteModal();
  };

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const response = await OrderManagementService.getOrder();
      setData(response);
      setFilteredData(response);
    } catch (error) {
      toast.error("Error fetching orders");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const renderPagination = () => {
    const maxPageButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    const pages = [];

    if (startPage > 1) {
      pages.push(
        <Button key={1} onClick={() => handlePageChange(1)}>
          1
        </Button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis-start">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          className={`pagination-button ${currentPage === i ? "active" : ""}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis-end">...</span>);
      }
      pages.push(
        <Button key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Button>
      );
    }

    return (
      <div className="pagination-container gap-2 d-flex align-items-center justify-content-end">
        <Button
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          Prev
        </Button>
        {pages}
        <Button
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Next
        </Button>
      </div>
    );
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
      <Row className="mb-4">
          <Col md={4}>
            <label className="form-label">Filter by Table</label>
            <Select
              value={selectedTable}
              onChange={setSelectedTable}
              options={tableOptions}
              isClearable
              placeholder="Select Table..."
              className="react-select"
            />
          </Col>
          <Col md={6}>
            <div className="d-flex gap-3">
              <div className="flex-grow-1">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  onChange={(e) => handleDateChange('start', e.target.value)}
                  max={endDate || undefined}
                />
              </div>
              <div className="flex-grow-1">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={endDate}
                  onChange={(e) => handleDateChange('end', e.target.value)}
                  min={startDate || undefined}
                />
              </div>
            </div>
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Button
              color="secondary"
              className="w-100"
              onClick={resetFilters}
            >
              Reset Filters
            </Button>
          </Col>
        </Row>

        {/* Display filter summary if any filters are active */}
        {(selectedTable || startDate || endDate) && (
          <div className="mb-3 p-2 bg-light rounded">
            <small className="text-muted">
              Filters: {' '}
              {selectedTable && <span className="badge bg-info me-2">Table: {selectedTable.label}</span>}
              {startDate && <span className="badge bg-info me-2">From: {new Date(startDate).toLocaleDateString()}</span>}
              {endDate && <span className="badge bg-info me-2">To: {new Date(endDate).toLocaleDateString()}</span>}
            </small>
          </div>
        )}
        <Row>
          {loading ? (
            <div className="loading-container d-flex flex-column justify-content-center align-items-center">
              <Triangle
                visible={true}
                height="80"
                width="80"
                color="#5B71B9"
                ariaLabel="triangle-loading"
              />
              <h5 className="mt-1">Loading...</h5>
            </div>
          ) : paginatedData.length > 0 ? (
            paginatedData.map((order) => {
              const totalImages = order.menuOrderDetails.length;
              const isExpanded = expandedOrderId === order.id;
              const imagesToShow = isExpanded 
                ? order.menuOrderDetails 
                : order.menuOrderDetails.slice(0, maxImagesToShow);
              const remainingImages = totalImages - maxImagesToShow;

              return (
                <Col key={order.id} lg={3} md={6} className="mb-4">
                  <Card 
                    className={`h-auto`}
                    style={{ 
                      borderWidth: '0px',
                      borderLeftWidth: '4px',
                      borderLeftColor: statusColors[order.billingStatus], // Apply status color to the left border
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                    onClick={() => onOrderSelect(order.id)}
                  >
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="card-title mb-0">
                          <span
                            style={{ cursor: "pointer" }}
                            className="text-primary"
                          >
                            {order.orderRef}
                          </span>
                        </h5>
                        <span className='badge rounded'
                          style={{ 
                            backgroundColor: statusColors[order.billingStatus], // Apply status color to the badge
                            color: '#fff', // Set text color to white for better contrast
                          }}
                        >
                          {order.billingStatus}
                        </span>
                      </div>

                      <div className="mb-3">
                        <Row className="mb-2">
                          <Col xs="6" className="text-muted">Total Amount:</Col>
                          <Col xs="6" className="text-end fw-medium">{order.totalAmount}</Col>
                        </Row>
                        <Row className="mb-2">
                          <Col xs="6" className="text-muted">Order Date:</Col>
                          <Col xs="6" className="text-end">{new Date(order.createdDate).toLocaleDateString()}</Col>
                        </Row>
                        <Row className='mb-2'>
                          <Col xs="6" className="text-muted">Table:</Col>
                          <Col xs="6" className="text-end">{order?.tableData?.identification}</Col>
                        </Row>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <button
                            className="btn btn-light btn-sm me-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(order);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-soft-danger btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(order);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                        {showImages && (
                          <div className="d-flex align-items-center">
                            <div 
                              className="d-flex"
                              style={{ 
                                marginRight: isExpanded ? '10px' : '0px'
                              }}
                            >
                              {imagesToShow.map((detail, index) => (
                                <div
                                  key={index}
                                  className='bg-light relative'
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    marginLeft: index === 0 ? '0' : '-8px',
                                    zIndex: imagesToShow.length - index,
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    border: '2px solid #fff',
                                  }}
                                >
                                  <img
                                    src={detail.menuItemDetail.cloudR2ImagePath?.getCDNUrl()}
                                    alt={`Product ${index + 1}`}
                                    style={{ 
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover'
                                    }}
                                  />
                                </div>
                              ))}
                              {remainingImages > 0 && !isExpanded && (
                                <div
                                  className='bg-light relative'
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    marginLeft: '-8px',
                                    zIndex: 0,
                                    borderRadius: '50%',
                                    border: '2px solid #fff',
                                    backgroundColor: '#000',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                  onClick={() => toggleExpandImages(order.id)}
                                >
                                  <span className="text-muted">+{remainingImages}</span>
                                </div>
                              )}
                            </div>
                            {isExpanded && (
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => toggleExpandImages(order.id)}
                              >
                                Hide
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })
          ) : (
            <p>No orders found.</p>
          )}
        </Row>
        {renderPagination()}
      </div>
      <EditStatus
        isOpen={editModal}
        toggle={() => setEditModal(!editModal)}
        selectedOrder={selectedOrder}
        status={editStatus}
        setStatus={setEditStatus}
        onSave={handleSaveEdit}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={toggleDeleteModal}
      />
    </div>
  );
};